// Pointer interaction layer for the v2 planner canvas. Behaviour depends on the
// active tool in the store. Mutations go through controller.commit() so undo +
// autosave + dirty-state are handled in one place.

import Konva from 'konva';
import { plantDiameter } from '../plants';

const MIN_SIZE = 4;

export default class Interactions {
  constructor(renderer, store, controller) {
    this.renderer = renderer;
    this.store = store;
    this.controller = controller;
    this.stage = renderer.stage;

    this.draw = null;   // in-progress drag-draw {tool, start}
    this.poly = null;   // in-progress polygon/line {tool, points}
    this.pan = null;    // in-progress pan
    this.lasso = null;  // in-progress lasso
    this._dragPrev = null;
    this._tfPrev = null;
  }

  get tool() { return this.store.state.activeTool; }
  get elements() { return this.store.state.elements; }
  get viewOnly() { return this.controller.viewOnlyValue; }

  snap(v) { const s = this.store.state.snapStep; return s ? Math.round(v / s) * s : v; }
  snapPos(p) { return { x: this.snap(p.x), y: this.snap(p.y) }; }

  bind() {
    const s = this.stage;
    s.on('mousedown touchstart', (e) => this.onDown(e));
    s.on('mousemove touchmove', (e) => this.onMove(e));
    s.on('mouseup touchend', (e) => this.onUp(e));
    s.on('click tap', (e) => this.onClick(e));
    s.on('dblclick dbltap', (e) => this.onDblClick(e));
    s.on('wheel', (e) => this.onWheel(e));

    // Touch: two-finger pinch-zoom + long-press context menu.
    s.on('touchstart', (e) => this.onTouchStart(e));
    s.on('touchmove', (e) => this.onTouchMove(e));
    s.on('touchend touchcancel', (e) => this.onTouchEnd(e));

    // Hover highlight (over a shape or its label).
    s.on('mouseover', (e) => {
      if (this.tool === 'navigate') { this.renderer.setHover(null); return; }
      const el = this.isElement(e.target) ? this.elementForNode(e.target)
        : this.isLabel(e.target) ? this.elementForLabel(e.target) : null;
      this.renderer.setHover(el?.data?.locked ? null : el);
    });
    s.on('mouseout', (e) => { if (this.isElement(e.target) || this.isLabel(e.target)) this.renderer.setHover(null); });

    // Right-click context menu.
    s.on('contextmenu', (e) => {
      e.evt.preventDefault();
      if (this.viewOnly) return;
      const el = this.isElement(e.target) ? this.elementForNode(e.target) : null;
      this.controller.openContextMenu(e.evt.clientX, e.evt.clientY, el);
    });

    // Node drag (move) — events bubble to the stage.
    s.on('dragstart', (e) => {
      const node = e.target;
      // Konva's Transformer auto-drags every attached node when you grab one —
      // ignore those sibling dragstarts (the lead owns the gesture + snapshot).
      if (this._group && this.isElement(node) && node.id() !== this._group.leadId) return;
      if (this.elementForNode(node)?.data?.locked) { node.stopDrag(); return; }
      this._dragPrev = this.controller.takeSnapshot();
      this.startGroupDrag(node);
    });
    s.on('dragmove', (e) => {
      if (this.isHandle(e.target)) { this.renderer.applyHandle(e.target); return; }
      if (this.isElement(e.target)) {
        const n = e.target;
        if (this.store.state.snapStep) n.position({ x: this.snap(n.x()), y: this.snap(n.y()) });
        this.dragGroup(n); // move the rest of a multi-selection by the same delta
        this.renderer.repositionLabels(); // labels + hover follow the shape
        this.renderer.offsetVertices(n); // poly/line vertex handles follow too
      }
      // dragging a label: Konva moves it freely; don't reposition it back
    });
    s.on('dragend', (e) => this.onDragEnd(e));

    // Transform (resize/rotate) via the Transformer.
    this.renderer.transformer.on('transformstart', () => { this._tfPrev = this.controller.takeSnapshot(); });
    this.renderer.transformer.on('transform', () => this.renderer.repositionLabels());
    this.renderer.transformer.on('transformend', () => this.onTransformEnd());
  }

  elementForNode(node) {
    const id = node?.id?.();
    return id ? this.elements.find((el) => el.id === id) : null;
  }

  isElement(node) { return node && node.name && node.name() === 'element'; }
  isLabel(node) { return node && node.name && node.name() === 'label'; }
  isHandle(node) { return node && node.name && node.name() === 'vhandle'; }
  elementForLabel(node) { const id = node?.getAttr?.('elId'); return id ? this.elements.find((el) => el.id === id) : null; }

  // --- pointer down ---------------------------------------------------------
  onDown(e) {
    if (e.evt.touches && e.evt.touches.length > 1) return; // multi-touch -> pinch
    this.pointerDownTool = this.tool;
    if (this.viewOnly) { this.startPan(); return; } // drag to pan; no editing
    const node = e.target;
    const onElement = this.isElement(node);
    const pos = this.renderer.pointerPlanPos();

    switch (this.tool) {
      case 'rectangle':
      case 'ellipse':
        if (!onElement) { this.startDraw(this.tool, pos); }
        break;
      case 'navigate':
        this.startPan();
        break;
      case 'select': {
        // Select an element on press (not click) so an immediate drag already has
        // the transformer + group. Labels are skipped here — re-selecting rebuilds
        // the label node and would kill its drag; they select on click instead.
        if (onElement) {
          const el = this.elementForNode(node);
          if (el && !el.data?.locked) {
            const additive = e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey;
            if (additive) this.select(el.id, true);
            else if (!this.store.state.selection.includes(el.id)) this.select(el.id, false);
            // else already selected — keep the (possibly multi-) selection for dragging
          }
        } else if (!this.isLabel(node) && !this.isHandle(node) && !this.poly) {
          this.startLasso(pos);
        }
        break;
      }
      default:
        break;
    }
  }

  onMove() {
    if (this._pinching) return; // a pinch owns the gesture
    const pos = this.renderer.pointerPlanPos();
    if (this.draw) this.updateDraw(pos);
    else if (this.poly) this.updatePoly(pos);
    else if (this.pan) this.updatePan();
    else if (this.lasso) this.updateLasso(pos);
    else if (this.tool === 'placement') this.updatePlacementGhost(pos);
    else if (this.tool === 'calibrate' && this._calib?.length === 1) this.updateCalibLine(pos);
  }

  updateCalibLine(cursor) {
    const a = this._calib[0];
    this.renderer.setPreview(new Konva.Line({
      points: [a.x, a.y, cursor.x, cursor.y],
      stroke: '#087332', strokeWidth: 2, strokeScaleEnabled: false, dash: [6, 4],
    }));
  }

  onUp() {
    if (this.draw) this.finishDraw();
    else if (this.pan) this.pan = null;
    else if (this.lasso) this.finishLasso();
  }

  // --- click (selection / erase / poly vertices) ----------------------------
  onClick(e) {
    if (this._lpFired) { this._lpFired = false; return; } // long-press already handled
    if (this.viewOnly) return;
    const pointerDownTool = this.pointerDownTool;
    this.pointerDownTool = null;
    if (this.tool === 'select' && pointerDownTool && pointerDownTool !== 'select') return;
    const node = e.target;
    if (this.isHandle(node)) return; // a click on a vertex handle never deselects
    const onElement = this.isElement(node);

    if (this.tool === 'placement') {
      const pos = this.snapPos(this.renderer.pointerPlanPos());
      this.controller.placeSpecies(pos.x, pos.y, e.evt.shiftKey); // Shift = keep placing
      return;
    }

    if (this.tool === 'calibrate') {
      const pos = this.renderer.pointerPlanPos();
      this._calib = this._calib || [];
      this._calib.push({ x: pos.x, y: pos.y });
      if (this._calib.length >= 2) {
        const [a, b] = this._calib; this._calib = null; this.renderer.clearPreview();
        this.controller.finishCalibration(a, b);
      }
      return;
    }

    if (this.tool === 'erase' && onElement) {
      const el = this.elementForNode(node);
      if (el && !el.data?.locked) this.controller.commit(this.controller.takeSnapshot(), () => {
        this.store.state.elements = this.elements.filter((x) => x !== el);
        this.store.set({ selection: [] });
      });
      return;
    }

    if ((this.tool === 'polygon' || this.tool === 'line')) {
      this.addPolyVertex(this.renderer.pointerPlanPos());
      return;
    }

    if (this.tool === 'select') {
      // A plain click (no drag — Konva suppresses click after a drag) finalises
      // selection: additive toggles were already handled on press, so here we
      // only collapse a multi-selection to the clicked element, or deselect.
      const additive = e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey;
      const el = onElement ? this.elementForNode(node) : (this.isLabel(node) ? this.elementForLabel(node) : null);
      if (el && !el.data?.locked && !additive) this.select(el.id, false);
      else if (!el && !additive && !this.lasso) this.store.set({ selection: [] });
    }
  }

  onDblClick(e) {
    if (this.poly) { this.finishPoly(); return; }
    const node = e.target;
    // Double-click empty canvas zooms in (works in view mode too).
    if (node === this.stage) {
      this.controller.zoomToScaleAt((this.store.state.view.scale || 1) * 1.5, this.stage.getPointerPosition());
      return;
    }
    if (this.viewOnly) return;
    // Double-click a vertex handle removes that vertex.
    if (this.isHandle(node) && node.getAttr('vkind') === 'anchor') {
      const prev = this.controller.takeSnapshot();
      if (this.renderer.removeVertex(node.getAttr('elId'), node.getAttr('vidx'))) this.controller.commit(prev);
      return;
    }
    if (this.isElement(node)) {
      const el = this.elementForNode(node);
      if (el?.data?.locked) return;
      const sel = this.store.state.selection;
      // On the selected polygon/line: insert a vertex on the nearest segment.
      if (el && (el.type === 'polygon' || el.type === 'line') && sel.length === 1 && sel[0] === el.id) {
        const pos = this.renderer.pointerPlanPos();
        const prev = this.controller.takeSnapshot();
        if (this.renderer.addVertexNear(el.id, pos.x, pos.y)) this.controller.commit(prev);
        return;
      }
      // On a shape: select all elements of the same type.
      if (el) this.controller.selectAllOfType(el.type);
    }
  }

  select(id, additive) {
    if (this.elements.find((el) => el.id === id)?.data?.locked) return;
    let sel = this.store.state.selection.slice();
    if (additive) sel = sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id];
    else sel = [id];
    this.store.set({ selection: sel });
  }

  // --- drag move ------------------------------------------------------------
  // Capture the other selected element nodes + start positions so a drag on one
  // moves the whole selection together.
  startGroupDrag(node) {
    this._group = null;
    if (!this.isElement(node)) return;
    const id = node.id();
    if (this.elementForNode(node)?.data?.locked) return;
    const sel = this.store.state.selection || [];
    if (sel.length < 2 || !sel.includes(id)) return;
    const others = [];
    for (const sid of sel) {
      if (sid === id || this.elements.find((el) => el.id === sid)?.data?.locked) continue;
      const n = this.renderer.nodeMap.get(sid);
      if (n) others.push({ node: n, x0: n.x(), y0: n.y() });
    }
    this._group = { leadId: id, leadX0: node.x(), leadY0: node.y(), others };
  }

  dragGroup(lead) {
    if (!this._group) return;
    const dx = lead.x() - this._group.leadX0;
    const dy = lead.y() - this._group.leadY0;
    for (const o of this._group.others) {
      // Transformer siblings are already moved by Konva's multi-drag; only nudge
      // the ones it doesn't (polygons/lines), so nothing moves twice.
      if (o.node.isDragging && o.node.isDragging()) continue;
      o.node.position({ x: o.x0 + dx, y: o.y0 + dy });
    }
  }

  onDragEnd(e) {
    const node = e.target;
    // Ignore the dragend echo from a sibling we stopDrag'd at gesture start.
    if (this._group && this.isElement(node) && node.id() !== this._group.leadId) return;
    if (!this._dragPrev) return;
    const prev = this._dragPrev; this._dragPrev = null;
    const group = this._group; this._group = null;

    if (this.isHandle(node)) { this.renderer.applyHandle(node); this.controller.commit(prev); return; }

    if (this.isLabel(node)) {
      const el = this.elementForLabel(node);
      const c = el && elementCenter(el);
      if (!c) return;
      el.data.labelX = node.x() - c.x;
      el.data.labelY = node.y() - c.y;
      this.controller.commit(prev);
      return;
    }
    const el = this.elementForNode(node);
    if (!el || el.data?.locked) return;
    this.renderer.bakeNode(node, el);
    // Bake the rest of a moved multi-selection too.
    if (group) for (const o of group.others) { const elx = this.elementForNode(o.node); if (elx && !elx.data?.locked) this.renderer.bakeNode(o.node, elx); }
    this.controller.commit(prev);
  }

  onTransformEnd() {
    if (!this._tfPrev) return;
    for (const node of this.renderer.transformer.nodes()) {
      const el = this.elementForNode(node);
      if (el && !el.data?.locked) this.renderer.bakeNode(node, el);
    }
    const prev = this._tfPrev; this._tfPrev = null;
    this.controller.commit(prev);
  }

  // --- draw rect / ellipse --------------------------------------------------
  startDraw(tool, start) {
    start = this.snapPos(start);
    this.draw = { tool, start, current: start };
    const node = tool === 'ellipse'
      ? new Konva.Ellipse({ x: start.x, y: start.y, radiusX: 0, radiusY: 0 })
      : new Konva.Rect({ x: start.x, y: start.y, width: 0, height: 0 });
    node.fill('rgba(8,115,50,0.12)'); node.stroke('#087332'); node.strokeWidth(1);
    this.renderer.setPreview(node);
  }

  updateDraw(pos) {
    pos = this.snapPos(pos);
    this.draw.current = pos;
    const n = this.renderer.previewNode; if (!n) return;
    const { start } = this.draw;
    if (this.draw.tool === 'ellipse') {
      n.position({ x: (start.x + pos.x) / 2, y: (start.y + pos.y) / 2 });
      n.radiusX(Math.abs(pos.x - start.x) / 2); n.radiusY(Math.abs(pos.y - start.y) / 2);
    } else {
      n.position({ x: Math.min(start.x, pos.x), y: Math.min(start.y, pos.y) });
      n.width(Math.abs(pos.x - start.x)); n.height(Math.abs(pos.y - start.y));
    }
    n.getLayer().batchDraw();
  }

  finishDraw() {
    const { tool, start, current } = this.draw;
    this.draw = null;
    this.renderer.clearPreview();
    const w = Math.abs(current.x - start.x); const h = Math.abs(current.y - start.y);
    if (w < MIN_SIZE && h < MIN_SIZE) return;
    const cx = (start.x + current.x) / 2; const cy = (start.y + current.y) / 2;
    const data = {
      x: cx, y: cy, width: w, height: tool === 'ellipse' ? w : h,
      fillColor: 'rgba(114, 180, 95, 1)', strokeColor: 'rgba(0, 0, 0, 1)', strokeWidth: null,
      labels: [], labelX: 0, labelY: 0, layer: null, addedDate: null, removedDate: null,
    };
    if (tool === 'rectangle') { data.height = h; data.rotation = 0; }
    const el = { id: `el-${Date.now()}-${Math.round(start.x)}`, type: tool, speciesIds: [], data };
    this.controller.commit(this.controller.takeSnapshot(), () => {
      this.store.state.elements = [...this.elements, el];
    });
    this.controller.applyTool('select');
    this.store.set({ selection: [el.id] });
    window.posthog?.capture('planner_element_created', { element_type: tool, planner_version: 'v2' });
  }

  // --- draw polygon / line (straight) --------------------------------------
  addPolyVertex(pos) {
    if (!this.poly) this.poly = { tool: this.tool, points: [] };
    this.poly.points.push([pos.x, pos.y]);
    this.renderPolyPreview(pos);
  }

  updatePoly(pos) { if (this.poly) this.renderPolyPreview(pos); }

  renderPolyPreview(cursor) {
    const pts = this.poly.points.flatMap((p) => p);
    if (cursor) pts.push(cursor.x, cursor.y);
    const node = new Konva.Line({ points: pts, stroke: '#087332', strokeWidth: 1, closed: this.poly.tool === 'polygon', dash: [4, 4] });
    this.renderer.setPreview(node);
  }

  finishPoly() {
    const poly = this.poly; this.poly = null; this.renderer.clearPreview();
    if (!poly || poly.points.length < 2) return;
    const coordinates = poly.points.map((p) => [p[0], p[1]]);
    const data = {
      coordinates, strokeColor: 'rgba(0, 0, 0, 1)', strokeWidth: poly.tool === 'line' ? 6 : null,
      fillColor: poly.tool === 'polygon' ? 'rgba(114, 180, 95, 1)' : null,
      labels: [], labelX: 0, labelY: 0, layer: null, addedDate: null, removedDate: null,
    };
    const el = { id: `el-${Date.now()}`, type: poly.tool, speciesIds: [], data };
    this.controller.commit(this.controller.takeSnapshot(), () => {
      this.store.state.elements = [...this.elements, el];
    });
    this.controller.applyTool('select');
    this.store.set({ selection: [el.id] });
    window.posthog?.capture('planner_element_created', { element_type: poly.tool, planner_version: 'v2' });
  }

  cancelPoly() { this.poly = null; this.renderer.clearPreview(); }

  // --- pan / zoom -----------------------------------------------------------
  startPan() {
    const p = this.stage.getPointerPosition();
    const v = this.store.state.view;
    this.pan = { sx: p.x, sy: p.y, px: v.posX, py: v.posY };
  }

  updatePan() {
    const p = this.stage.getPointerPosition();
    const view = { ...this.store.state.view, posX: this.pan.px + (p.x - this.pan.sx), posY: this.pan.py + (p.y - this.pan.sy) };
    this.store.set({ view }, { source: 'pan' });
  }

  // --- lasso select ---------------------------------------------------------
  startLasso(start) { this.lasso = { start, current: start }; }

  updateLasso(pos) {
    this.lasso.current = pos;
    const { start } = this.lasso;
    const node = new Konva.Rect({
      x: Math.min(start.x, pos.x), y: Math.min(start.y, pos.y),
      width: Math.abs(pos.x - start.x), height: Math.abs(pos.y - start.y),
      fill: 'rgba(8,115,50,0.06)', stroke: '#087332', strokeWidth: 1, dash: [4, 4],
    });
    this.renderer.setPreview(node);
  }

  finishLasso() {
    const { start, current } = this.lasso; this.lasso = null; this.renderer.clearPreview();
    const x1 = Math.min(start.x, current.x); const x2 = Math.max(start.x, current.x);
    const y1 = Math.min(start.y, current.y); const y2 = Math.max(start.y, current.y);
    if (x2 - x1 < 3 && y2 - y1 < 3) return;
    const sel = [];
    for (const el of this.elements) {
      const c = elementCenter(el);
      if (!el.data?.locked && c && c.x >= x1 && c.x <= x2 && c.y >= y1 && c.y <= y2) sel.push(el.id);
    }
    this.store.set({ selection: sel });
  }

  updatePlacementGhost(pos) {
    const sp = this.store.state.pendingSpecies;
    if (!sp) return;
    const r = plantDiameter(sp) / 2;
    this.renderer.setPreview(new Konva.Ellipse({
      x: pos.x, y: pos.y, radiusX: r, radiusY: r,
      fill: 'rgba(114,180,95,0.4)', stroke: '#087332', strokeWidth: 1, dash: [4, 4],
    }));
  }

  // --- touch: pinch-zoom + long-press --------------------------------------
  onTouchStart(e) {
    const touches = e.evt.touches;
    if (touches && touches.length === 2) { this.startPinch(touches); return; }
    if (this.viewOnly) return;
    const t = touches && touches[0];
    if (!t) return;
    const el = this.isElement(e.target) ? this.elementForNode(e.target) : null;
    this._lp = { x: t.clientX, y: t.clientY, el };
    clearTimeout(this._lpTimer);
    this._lpTimer = setTimeout(() => {
      this._lpFired = true;
      this.draw = this.lasso = this.pan = null; this.renderer.clearPreview();
      this.renderer.stopAllDrag();
      if (this._lp) this.controller.openContextMenu(this._lp.x, this._lp.y, this._lp.el);
    }, 500);
  }

  onTouchMove(e) {
    const touches = e.evt.touches;
    if (touches && touches.length === 2) { e.evt.preventDefault(); this.updatePinch(touches); return; }
    const t = touches && touches[0];
    if (this._lp && t && Math.hypot(t.clientX - this._lp.x, t.clientY - this._lp.y) > 10) this.cancelLongPress();
  }

  onTouchEnd(e) {
    const n = e.evt.touches ? e.evt.touches.length : 0;
    if (n < 2) { this._pinching = false; this._pinchDist = 0; this._pinchMid = null; }
    this.cancelLongPress();
  }

  cancelLongPress() { clearTimeout(this._lpTimer); this._lpTimer = null; this._lp = null; }

  startPinch(touches) {
    this._pinching = true;
    this.cancelLongPress();
    this.draw = this.lasso = this.pan = null; this.renderer.clearPreview();
    this.renderer.stopAllDrag();
    const m = this.pinchMetrics(touches);
    this._pinchDist = m.dist; this._pinchMid = m.mid;
  }

  updatePinch(touches) {
    this._pinching = true;
    const m = this.pinchMetrics(touches);
    if (!this._pinchDist) { this._pinchDist = m.dist; this._pinchMid = m.mid; return; }
    const view = this.store.state.view;
    const old = view.scale || 1;
    const scale = Math.min(50, Math.max(0.02, old * (m.dist / this._pinchDist)));
    const mx = (m.mid.x - view.posX) / old;
    const my = (m.mid.y - view.posY) / old;
    // zoom around the pinch midpoint, and pan by however far the midpoint moved
    const posX = m.mid.x - mx * scale + (m.mid.x - this._pinchMid.x);
    const posY = m.mid.y - my * scale + (m.mid.y - this._pinchMid.y);
    this.store.set({ view: { scale, posX, posY } }, { source: 'zoom' });
    this._pinchDist = m.dist; this._pinchMid = m.mid;
  }

  pinchMetrics(touches) {
    const r = this.stage.container().getBoundingClientRect();
    const a = touches[0]; const b = touches[1];
    return {
      dist: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY),
      mid: { x: (a.clientX + b.clientX) / 2 - r.left, y: (a.clientY + b.clientY) / 2 - r.top },
    };
  }

  onWheel(e) {
    e.evt.preventDefault();
    const view = this.store.state.view;
    const old = view.scale || 1;
    const pointer = this.stage.getPointerPosition();
    const mx = (pointer.x - view.posX) / old; const my = (pointer.y - view.posY) / old;
    const factor = e.evt.deltaY > 0 ? 0.92 : 1.08;
    const scale = Math.min(50, Math.max(0.02, old * factor));
    this.store.set({ view: { scale, posX: pointer.x - mx * scale, posY: pointer.y - my * scale } }, { source: 'zoom' });
  }
}

function elementCenter(el) {
  const d = el.data || {};
  if (el.type === 'ellipse' || el.type === 'rectangle' || el.type === 'image') return { x: d.x, y: d.y };
  const pts = (d.coordinates || []).filter((c) => Array.isArray(c) && typeof c[0] === 'number');
  if (!pts.length) return null;
  const sx = pts.reduce((a, c) => a + c[0], 0); const sy = pts.reduce((a, c) => a + c[1], 0);
  return { x: sx / pts.length, y: sy / pts.length };
}
